import { useEffect } from 'react';
import i18n from '@/i18n';
import { toast } from '@/lib/toast';
import { useDataStore } from '@/stores/dataStore';

/**
 * Global keyboard shortcut: press `J` (no modifiers) to jump to the next
 * channel where someone has @mentioned you.
 *
 * Why this is a wow-fishka: Discord's Cmd+K palette is a search — you
 * still have to look at unread badges and decide where to go. `J` is
 * one-handed triage: skip the eyeballing, the app already knows which
 * channels are screaming at you. Cycles through guilds in display order,
 * checking every channel; falls back to the DM list if no guild has
 * mentions.
 *
 * Skipped when focus is in any editable surface (composer / inputs /
 * contenteditable) so the user can still type "j" in messages, role
 * names, etc.
 */
export function MentionJumpHotkey() {
  // We re-read state inside the listener so the snapshot is fresh on
  // each keystroke; subscribing reactively would just churn the effect.
  useEffect(() => {
    function isEditableTarget(t: EventTarget | null): boolean {
      if (!(t instanceof HTMLElement)) return false;
      const tag = t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (t.isContentEditable) return true;
      // Radix / cmdk popovers + menus also count as "interactive" for
      // the purpose of swallowing this hotkey, so the J inside a search
      // doesn't yank the user away.
      if (t.closest('[role="dialog"], [role="menu"], [role="listbox"], [role="combobox"]')) {
        return true;
      }
      return false;
    }
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'j' && e.key !== 'J' && e.key !== 'о' && e.key !== 'О') return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isEditableTarget(e.target)) return;

      const state = useDataStore.getState();
      const { mentionByChannel, guilds, guildOrder, dmChannels, currentGuildId } = state;

      // Build ordered candidate list: current guild first (so J keeps
      // chewing through *this* server's mentions before jumping away),
      // then the rest of guilds in their on-screen order, then DMs.
      const seen = new Set<string>();
      const candidates: string[] = [];
      function pushIfMention(channelId: string) {
        if (seen.has(channelId)) return;
        seen.add(channelId);
        if ((mentionByChannel[channelId] ?? 0) > 0) {
          candidates.push(channelId);
        }
      }
      if (currentGuildId && guilds[currentGuildId]) {
        for (const ch of guilds[currentGuildId].channels) pushIfMention(ch.id);
      }
      for (const guildId of guildOrder) {
        if (guildId === currentGuildId) continue;
        const g = guilds[guildId];
        if (!g) continue;
        for (const ch of g.channels) pushIfMention(ch.id);
      }
      for (const dmId of Object.keys(dmChannels)) {
        pushIfMention(dmId);
      }

      // We've already decided this keystroke is "ours" (J, no modifiers,
      // not in an editable target) — eat it BEFORE branching on whether
      // there's a candidate. Otherwise a future J-bound shortcut in
      // another listener could double-fire on the "no mentions" path.
      e.preventDefault();

      if (candidates.length === 0) {
        // Silent toast — friendly miss feedback so the user knows the key
        // was recognised, just nothing to jump to.
        toast.message(i18n.t('ui.mentionJump.noMentions'), {
          description: i18n.t('ui.mentionJump.allRead'),
          duration: 1800,
        });
        return;
      }

      // Pick the first candidate that *isn't* the current channel — so
      // pressing J repeatedly cycles through mentions instead of getting
      // stuck on the same one.
      const target =
        candidates.find((id) => id !== state.currentChannelId) ?? candidates[0]!;

      // Route the jump: if the target is a DM, use selectDirectChannel
      // (it also clears currentGuildId). If it's a guild channel, find
      // which guild owns it and selectGuild + selectChannel.
      if (dmChannels[target]) {
        state.selectDirectChannel(target);
        return;
      }
      const owningGuild = guildOrder.find((gid) =>
        guilds[gid]?.channels.some((c) => c.id === target),
      );
      if (owningGuild) {
        if (owningGuild !== currentGuildId) state.selectGuild(owningGuild);
        state.selectChannel(target);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return null;
}
